import { create } from 'zustand';
import { api } from '../api';
import type { CurrentUser, Role } from '../types';

const NAME_STORAGE_KEY = 'mypos.till.user.name';

function readCachedName(): string | null {
  try { return localStorage.getItem(NAME_STORAGE_KEY); } catch { return null; }
}
function writeCachedName(name: string | null) {
  try {
    if (name) localStorage.setItem(NAME_STORAGE_KEY, name);
    else      localStorage.removeItem(NAME_STORAGE_KEY);
  } catch { /* ignore */ }
}

export interface DataMismatch {
  oldServerUrl: string | null;
  oldStoreId: string | null;
  newServerUrl: string;
  newStoreId: string;
  unsyncedCount: number;
}

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
  mismatch: DataMismatch | null;
  hydrate(): Promise<void>;
  loginEmail(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  resolveMismatch(choice: 'archive' | 'cancel'): Promise<void>;
  can(permission: string): boolean;
  hasRole(...roles: Role[]): boolean;
}

let authClearedSubscribed = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  loading: true,
  error: null,
  mismatch: null,

  async hydrate() {
    // Subscribe once to main-process auth-cleared events. Fires when the IPC
    // wipes tokens after a failed refresh — without this the renderer keeps
    // showing the user as signed in while every api:get returns Unauthorized.
    if (!authClearedSubscribed) {
      authClearedSubscribed = true;
      api.auth.onCleared(() => {
        writeCachedName(null);
        set({ user: null, error: null });
      });
    }
    try {
      const raw = await api.auth.me() as any;
      // Detect the data-mismatch sentinel returned when the till's local data
      // belongs to a different store than the one the user is logging into.
      if (raw && (raw as any).__dataMismatch) {
        set({ mismatch: (raw as any).__dataMismatch as DataMismatch, user: null, loading: false });
        return;
      }
      const me = raw as CurrentUser | null;
      // auth:me IPC doesn't include name (it reads from local settings which
      // only cache userId/role/storeId). Fall back to the last name we cached
      // from the original login flow, which survives reloads via localStorage.
      const user = me ? { ...me, name: me.name ?? readCachedName() } : null;
      set({ user, loading: false, error: null });
    } catch (e) {
      set({ user: null, loading: false, error: (e as Error).message });
    }
  },

  async loginEmail(email, password) {
    set({ error: null });
    try {
      const result = await api.auth.login(email, password) as any;
      // Detect the data-mismatch sentinel before treating the result as a
      // normal LoginResult. The main process returns this when local data
      // belongs to a different store and there are unsynced sales.
      if (result && result.__dataMismatch) {
        set({ mismatch: result.__dataMismatch as DataMismatch, user: null });
        return;
      }
      const name = result.name ?? result.email ?? null;
      writeCachedName(name);
      const me = await api.auth.me() as any;
      if (me && me.__dataMismatch) {
        set({ mismatch: me.__dataMismatch as DataMismatch, user: null });
        return;
      }
      set({ user: me ? { ...me, name: me.name ?? name } : null, error: null });
    } catch (e) {
      set({ error: (e as Error).message });
      throw e;
    }
  },

  async resolveMismatch(choice) {
    const m = get().mismatch;
    if (!m) return;
    if (choice === 'archive') {
      // IMP-2: show loading splash immediately so App never flashes Login
      // between the archive completing and the re-hydrated user being set.
      set({ loading: true, mismatch: null });
      await api.auth.resolveDataMismatch('archive', m.newServerUrl, m.newStoreId);
      await get().hydrate(); // sets user + loading: false
      return;
    }
    // cancel: dismiss the mismatch modal and drop back to Login.
    await api.auth.resolveDataMismatch('cancel', m.newServerUrl, m.newStoreId);
    set({ mismatch: null, user: null });
  },

  async logout() {
    try {
      await api.auth.logout();
    } finally {
      writeCachedName(null);
      set({ user: null });
    }
  },

  can(permission) {
    const u = get().user;
    if (!u) return false;
    if (u.role === 'manager') return true;
    return u.permissions.includes(permission);
  },

  hasRole(...roles) {
    const r = get().user?.role;
    return r ? roles.includes(r) : false;
  },
}));
